import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/common";
import { useManageUserPageData, useUserListDerived } from "../hooks";
import PermissionOverridesSection from "./PermissionOverridesSection";
import { OrgTreeNodeBreadcrumb } from "@/components/common/OrgTreeNodeBreadcrumb";
import { CLIENT_ROLE_GROUPS } from "../utils/roles-permissions";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const ManageUserForm = observer(() => {
  const { t } = useTran();
  const { userId } = useParams();
  const {
    loading,
    errorMessage,
    handleRetry,
    editForm,
    updateEditField,
    roleOptions,
    loadingRoles,
    statusOptions,
    isCompanyAdmin,
    usernameError,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
    permissionModules,
    saving,
    meta,
    formatDateTime,
    handleChange,
    handleSelectChange,
    handleCompanyChange,
    handlePermissionOverridesChange,
    handleSubmit,
  } = useManageUserPageData({ userId });

  const {
    rolesWithPermissions,
    buildAllowAllPermissions,
    rolePermissionsToAllow,
  } = useUserListDerived();

  const parentCompanyId = useMemo(() => {
    const parentNode = organizationTree.find(
      (node) => node.unitLevel === "parent_company",
    );
    return parentNode?.id || parentNode?._id || parentNode?.key || null;
  }, [organizationTree]);

  const orgSelectValue = useMemo(() => {
    const ids = [...(editForm.organizationUnitId || [])];
    if (editForm.assignedToParentCompany && parentCompanyId) {
      if (!ids.includes(parentCompanyId)) ids.unshift(parentCompanyId);
    }
    return ids;
  }, [
    editForm.organizationUnitId,
    editForm.assignedToParentCompany,
    parentCompanyId,
  ]);

  const handleOrgChange = (selectedIds) => {
    const isParentSelected =
      parentCompanyId && selectedIds.includes(parentCompanyId);
    const orgUnitIds = parentCompanyId
      ? selectedIds.filter((id) => id !== parentCompanyId)
      : selectedIds;
    updateEditField(userId, "assignedToParentCompany", isParentSelected);
    updateEditField(userId, "organizationUnitId", orgUnitIds);
  };

  useEffect(() => {
    const roleKey = editForm.role;
    const mode = editForm.permissionOverrides?.mode;

    if (!roleKey) return;
    if (mode !== "inherit") return;

    const selectedRole = rolesWithPermissions.find(
      (r) => r.name === roleKey || r._id === roleKey,
    );

    if (!selectedRole) return;

    const permissions = selectedRole.isPermitAll
      ? buildAllowAllPermissions(permissionModules)
      : selectedRole.permissions || [];

    const allow = rolePermissionsToAllow(permissions);

    updateEditField(userId, "permissionOverrides", {
      mode: "inherit",
      allow,
      deny: [],
    });
  }, [editForm.role, editForm.permissionOverrides?.mode, rolesWithPermissions]);

  useEffect(() => {
    const mode = editForm.permissionOverrides?.mode;
    if (mode === "override") {
      updateEditField(userId, "permissionOverrides", {
        mode: "override",
        allow: [],
        deny: [],
      });
    }
  }, [editForm.permissionOverrides?.mode]);

  if (!userId) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("Select a user to view details.")}
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("Loading user details...")}
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="space-y-3 rounded-radius border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <p>{errorMessage}</p>
        <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  const emptyRoleLabel = t("No roles available.");
  const emptyCompanyLabel = t("No companies available");
  const showCustomRoleNameField = editForm.role === "super_admin";
  //To disable role select and company select for custom roles that user created on business unit level
  const isDisabledRoleAndCompannySelect =
    isCompanyAdmin && !CLIENT_ROLE_GROUPS.has(editForm.role);

  return (
    <form
      id="manage-user-form"
      onSubmit={handleSubmit}
      className="flex h-full min-h-0 flex-col overflow-hidden gap-6"
    >
      {/* Main Grid */}
      <div className="grid min-h-0 flex-1 gap-6 overflow-hidden md:grid-cols-2">
        {/* LEFT COLUMN */}
        <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-foreground">
              {t("Display name")}
              <span className="ml-1 text-destructive">*</span>
            </label>

            <Input
              value={editForm.name}
              onChange={handleChange("name")}
              placeholder={t("Enter full name")}
              autoComplete="name"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-foreground">
              {t("Email address")}
              <span className="ml-1 text-destructive">*</span>
            </label>

            <Input
              type="email"
              value={editForm.email}
              onChange={handleChange("email")}
              placeholder={t("Enter email address")}
              autoComplete="email"
            />
          </div>

          {showCustomRoleNameField && (
            <div className="flex flex-col gap-2 animate-in fade-in duration-200">
              <label className="block text-sm font-medium text-foreground">
                {t("Custom role name")}
              </label>

              <Input
                value={editForm.customRoleName || ""}
                onChange={handleChange("customRoleName")}
                placeholder={t("Enter custom role name")}
              />
            </div>
          )}

          {isCompanyAdmin ? (
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-foreground">
                {t("Company Name")}
                <span className="ml-1 text-destructive">*</span>
              </label>

              <SelectField
                id="companyId"
                value={editForm.companyId || undefined}
                onChange={handleCompanyChange}
                options={companyOptions}
                placeholder={t("Select a company")}
                disabled={companyLoading || isDisabledRoleAndCompannySelect}
                emptyLabel={emptyCompanyLabel}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-foreground">
                {t("Organization")}
                <span className="ml-1 text-destructive">*</span>
              </label>

              <OrgTreeNodeBreadcrumb
                tree={organizationTree}
                value={orgSelectValue}
                onChange={handleOrgChange}
                placeholder={t("Select organizations")}
                disabled={organizationLoading}
                includeParentCompany={true}
              />
            </div>
          )}

          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 border-t border-border/40 pt-4">
            <div className="flex flex-col gap-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {t("Created at")}
              </p>

              <p className="text-xs font-medium text-foreground">
                {meta.createdAt
                  ? formatDate(meta.createdAt)
                  : t("Not available")}
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {t("Last updated")}
              </p>

              <p className="text-xs font-medium text-foreground">
                {meta.updatedAt
                  ? formatDate(meta.updatedAt)
                  : t("Not available")}
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex min-h-0 flex-col overflow-hidden">
          <div className="flex flex-col gap-4 shrink-0">
            <div className="flex flex-col gap-2 ">
              <label className="block text-sm font-medium text-foreground">
                {t("Username")}
                <span className="ml-1 text-destructive">*</span>
              </label>

              <Input
                value={editForm.username}
                onChange={handleChange("username")}
                placeholder={t("Enter username")}
                autoComplete="username"
              />

              {usernameError && (
                <p className="text-xs text-destructive">{usernameError}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-foreground">
                {t("Role")}
                <span className="ml-1 text-destructive">*</span>
              </label>

              <SelectField
                id="role"
                value={editForm.role || undefined}
                onChange={(input) => {
                  const value = input?.target ? input.target.value : input;
                  handleSelectChange("role")(input);

                  if (value !== "super_admin") {
                    updateEditField(userId, "customRoleName", "");
                  }
                }}
                options={roleOptions}
                placeholder={t("Select a role")}
                disabled={loadingRoles || isDisabledRoleAndCompannySelect}
                emptyLabel={emptyRoleLabel}
              />
            </div>
          </div>

          {/* Permissions */}
          <div className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden">
            <label className="mb-2 block text-sm font-medium text-foreground">
              {t("Permissions")}
              <span className="ml-1 text-destructive">*</span>
            </label>

            <div className="min-h-0 flex-1 overflow-hidden">
              <PermissionOverridesSection
                value={editForm.permissionOverrides}
                modules={permissionModules}
                onChange={handlePermissionOverridesChange}
                checkBoxDisable={
                  editForm.permissionOverrides?.mode === "inherit"
                }
                userRole={editForm.role}
                isUserCreatedRole={false}
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
});

export default ManageUserForm;
